"use client";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

const VerticalCarousel = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute("data-index"));
            setActiveIndex(index);
          }
        });
      },
      { threshold: 0.6 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  return (
    <main className="bg-gradient-to-b from-green-50 to-green-100 rounded-xl py-10 px-4">
      {/* Title + Desc */}
      <div className="text-center max-w-7xl mx-auto mb-10">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-2xl md:text-3xl lg:text-4xl font-bold text-green-800"
        >
          {data.title} 
        </motion.h1>
        <p className="text-gray-700 mt-4 text-base font-semibold md:text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: data.description }}

        />
      </div>

      {/* Carousel */}
      <div className="relative flex justify-center">
        <div className="h-[70vh] w-full md:w-[85%] lg:w-[75%] overflow-y-scroll snap-y snap-mandatory scroll-smooth hide-scrollbar">
          {data.images.map((card, index) => {
            const isActive = index === activeIndex;
            return (
              <section
                key={index}
                data-index={index}
                ref={(el) => (sectionsRef.current[index] = el)}
                className="h-[70vh] w-full snap-center flex flex-col lg:flex-row items-center justify-center gap-8 bg-white/80 backdrop-blur-md rounded-2xl shadow-lg p-6 md:p-10 transition-all duration-500 hover:shadow-2xl"
              >
                {/* Left - Image */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{
                    opacity: 1,
                    scale: isActive ? 1 : 0.9,
                  }}
                  transition={{ duration: 0.5 }}
                  className="w-full lg:w-1/2 flex justify-center"
                >
                  <Image
                    src={card.src}
                    alt={card.label}
                    width={500}
                    height={500}
                    className="w-full max-w-md h-auto object-contain rounded-xl shadow-md"
                  />
                </motion.div>

                {/* Right - Text */}
                <AnimatePresence mode="wait">
                  {isActive && (
                    <motion.div
                      key={card.label}
                      initial={{ opacity: 0, x: 40 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.6 }}
                      className="w-full lg:w-1/2"
                    >
                      <h2 className="text-xl md:text-2xl font-semibold text-green-700 mb-4">
                        {card.label}
                      </h2>
                      <div className="space-y-3">
                        {card.desc.map((line, idx) => (
                          <p
                            key={idx}
                            className="text-gray-700 text-sm md:text-base font-semibold leading-relaxed"
                          
                              dangerouslySetInnerHTML={{ __html: line }}

                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default VerticalCarousel;
